<script setup lang="ts">
import { type FileStorage, fileToBase64 } from '~/utils/files/fileStorage'
import type { MaybeArray } from '~/types/utils'
import { useInjectedColor } from '~/composables/useInjectedColor'
import { toArray } from '~/utils/helpers'
import { humanizeList } from 'nano-string-utils'
import type { FileUploadProps } from '#ui/components/FileUpload.vue'

type FileDropZoneEmits = {
  filesDropped: [files: File[]]
  reset: []
}

interface FileDropZoneProps {
  id?: string
  disabled?: boolean
  showReset?: boolean
  ui?: FileUploadProps['ui']
}

const emits = defineEmits<FileDropZoneEmits>()
const props = defineProps<FileDropZoneProps>()

const { t } = useI18n('components.molecules.file.file-drop-zone')

const localeStore = useLocaleStore()

const viewport = useViewport()

const fileStorage = defineModel<FileStorage>({ required: true })

const fileValue = computed<File | null>({
  get() {
    return fileStorage.value.file?.value?.file ?? null
  },
  set(file: File | null) {
    if (file) {
      fileStorage.value.addFile(file, false)
    } else {
      fileStorage.value.removeFile()
    }
  }
})

const acceptFormats = computed(() => fileStorage.value.getFileTypes().join(','))
const acceptFormatsHumanReadable = computed(() => {
  const formats = fileStorage.value.getFileTypes()
  const formatNames = formats.map((format) => {
    const [_, name] = format.split('/')
    return name.toUpperCase()
  })

  const humanFormatNames = humanizeList(formatNames, {
    conjunction: localeStore.localeIsDutch ? 'of' : 'or',
    oxford: !localeStore.localeIsDutch
  })
  const maxFileSize = fileStorage.value.humanReadableMaxFileSize

  if (maxFileSize) {
    return `${humanFormatNames} (Max. ${maxFileSize})`
  } else {
    return humanFormatNames
  }
})

const imagePreviewUrl = ref<string>()

const $field = useInjectedColor()
const computedBorderColor = computed(() => {
  if ($field.color?.value) {
    return `border-${$field.color.value}`
  }
  return ''
})

const computedUi = computed(() =>
  nuxtUiMerge(props.ui, {
    base: `bg-accented ${computedBorderColor.value}`,
    files: `bg-accented `,
    file: computedBorderColor.value,
    fileTrailingButton: 'cursor-pointer'
  })
)

const isDesktop = computed(() => viewport.isGreaterOrEqual('desktop'))

const openFileBrowserColumnClass = computed(() => {
  const hasFile = !!fileStorage.value.file.value

  if (!hasFile) {
    if (props.showReset) {
      return tw`half xl:four-fifths`
    } else {
      return tw`full`
    }
  } else {
    if (props.showReset) {
      return tw`full xl:three-fifths`
    } else {
      return tw`half xl:four-fifths`
    }
  }
})

const clearFiles = () => {
  fileStorage.value.removeFile()
}

const onDrop = async (files: MaybeArray<File> | null) => {
  if (props.disabled) {
    return
  }

  if (files) {
    files = toArray(files)

    await Promise.all(
      files
        .filter((f) => f instanceof File) // Filter out non files
        .map(async (file) => {
          const base64String = await fileToBase64(file)
          fileStorage.value.addFile(file, false, base64String)
        })
    )
    emits('filesDropped', files)
  }
}

watch(
  () => fileStorage.value.file.value,
  (newValue) => {
    const file = newValue?.file
    if (!file) {
      imagePreviewUrl.value = undefined
      return
    }

    if (['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      const reader = new FileReader()
      reader.addEventListener('load', (e) => {
        imagePreviewUrl.value = e.target?.result?.toString() ?? undefined
      })
      reader.readAsDataURL(file)
    } else {
      imagePreviewUrl.value = undefined
    }
  },
  { immediate: true }
)

const { open: internalOpenFileDialog, onChange } = useFileDialog({
  multiple: false
})

const openFileDialog = () => {
  if (!props.disabled) {
    internalOpenFileDialog()
  }
}

onChange((files) => {
  if (files) {
    onDrop(Array.from(files))
  }
})
</script>

<template>
  <UFileUpload
    :id
    v-model="fileValue"
    :accept="acceptFormats"
    :label="fileStorage.file.value ? t('.upload-file') : t('.inactive-drop-zone')"
    :description="acceptFormatsHumanReadable"
    size="xl"
    :interactive="false"
    variant="area"
    layout="list"
    :ui="computedUi"
  >
    <!-- Remove icon -->
    <template #leading>
      <span />
    </template>
    <template #actions>
      <div class="columns mx-0 mobile w-full multiline">
        <div class="column pb-0" :class="openFileBrowserColumnClass">
          <VButton
            width="full"
            icon-left="fa-cloud-upload-alt"
            color="black"
            dark-mode-color="beige"
            override-color
            :disabled="disabled"
            :label="isDesktop ? '.choose-file' : undefined"
            @click="openFileDialog"
          />
        </div>

        <div v-if="fileStorage.file.value" class="column pb-0 half xl:one-fifth">
          <VButton
            width="full"
            icon-only="fa-x"
            color="danger"
            override-color
            :disabled="disabled"
            @click="clearFiles"
          />
        </div>

        <div v-if="showReset" class="column pb-0 half empty:hidden xl:one-fifth">
          <!-- TODO: Currently no text-warning-text exists, so manually override text colour to black -->
          <VButton
            width="full"
            icon-only="fa-arrows-rotate"
            color="warning"
            override-color
            class="text-black"
            :disabled="disabled"
            @click="emits('reset')"
          />
        </div>
      </div>
    </template>
  </UFileUpload>
</template>

<style scoped></style>
